import axios from 'axios';
import { getApiBaseUrl } from '../utils/api';

const API_BASE_URL = getApiBaseUrl();

// Create axios instance
const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            window.location.href = '/app/login';
        }
        return Promise.reject(error);
    }
);

/**
 * MLM Network Service
 * Handles API calls for MLM network visualization and performance reporting
 */
export const mlmService = {
    /**
     * Get node performance data for MLM visualization
     * @param {number|null} userId - User ID to get performance for (null = authenticated user)
     * @returns {Promise} Performance data
     */
    getNodePerformance: async (userId = null) => {
        try {
            console.log('mlmService: getNodePerformance called with userId:', userId, 'Type:', typeof userId);

            // Build request payload
            const payload = {};

            // Only include user_id if it's provided and valid
            if (userId !== null && userId !== undefined) {
                // Ensure it's an integer
                payload.user_id = parseInt(userId, 10);
                console.log('mlmService: Parsed userId to:', payload.user_id, 'Type:', typeof payload.user_id);
            }

            console.log('mlmService: Sending payload:', payload);
            const response = await axiosInstance.post('/tree/mlm/node-performance', payload);
            console.log('mlmService: Response received:', response.data);
            return response.data;
        } catch (error) {
            console.error('mlmService: API error:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Search users in MLM network by name, email, or mobile
     * @param {string} query - Search query (minimum 2 characters)
     * @param {number} limit - Maximum number of results (default: 10)
     * @returns {Promise} Search results
     */
    searchUsers: async (query, limit = 10) => {
        try {
            console.log('mlmService: searchUsers called with query:', query, 'limit:', limit);

            const response = await axiosInstance.get('/tree/mlm/search', {
                params: {
                    q: query,
                    limit: limit
                }
            });

            console.log('mlmService: searchUsers response:', response.data);
            return response.data;
        } catch (error) {
            console.error('mlmService: searchUsers error:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get hierarchical path from root to target user (for auto-navigation)
     * @param {number} userId - Target user ID
     * @returns {Promise} Path data with user hierarchy
     */
    getUserPath: async (userId) => {
        try {
            console.log('mlmService: getUserPath called with userId:', userId);

            const parsedUserId = parseInt(userId, 10);
            const response = await axiosInstance.get(`/tree/mlm/path/${parsedUserId}`);

            console.log('mlmService: getUserPath response:', response.data);
            return response.data;
        } catch (error) {
            console.error('mlmService: getUserPath error:', error);
            throw error.response?.data || error;
        }
    }
};

export default mlmService;
